// @flow

import {
  LinkClickedEvent,
  LozengeClickedEvent,
  MarketplaceAnalyticsContext,
  MenuExpandedEvent,
  MenuItemClickedEvent,
  TabClickedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import {
  AppListing,
  AppListingTab,
  ELEMENT_ID,
  MediaViewerResource,
} from '@atlassian/mpac-app-listing';
import Telemetry from '@atlassian/mpac-telemetry';
import { AppDeployment, HostingTypeToQueryParam } from '@atlassian/mpac-types';
import {
  AtlassianProductHostingTypeValues,
  MarketplaceAppPaymentModelValues,
} from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { useFeatureFlags } from '@atlassian/mpac-ui';
import { DOMUtils, UrlUtils } from '@atlassian/mpac-utils';
import capitalize from 'lodash/capitalize';
import React, { useLayoutEffect } from 'react';

import {
  safeToForwardToPartner,
  trackDownloadLink,
  trackLightboxView,
  trackLookingForDataCenter,
  trackSeeMoreReviews,
  trackVendorSupportLinkClick,
  trackWatchAction,
} from '../../../../../../../../common/utils/analytics/analytics';
import {
  useAppListingPageAnalytics,
  useGoogleAnalyticsExtraContext,
  useVendorGoogleAnalytics,
} from '../../../../../../../../hooks';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_APP_HEADER,
  ANALYTICS_EVENT_CONTAINER_NAV_BAR,
  ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
  ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
  ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
  ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
  ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
  ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
} from '../../../../../../../analytics/Events';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import useCloudMigrationInfo from '../../../../../../../hooks/useCloudMigration';
import useLoggedInUser from '../../../../../../../hooks/useLoggedInUser';
import AdminHeaderBanners from '../AdminHeaderBanners/AdminHeaderBanners';
import AdminHeaderLinks from '../AdminHeaderLinks';
import AppListingJSDWidget from '../AppListingJSDWidget';
import AppListingMeta from '../AppListingMeta/AppListingMeta';
import {
  getEmceeAppListingInstallLink,
  getEmceeAppListingRequestAppLink,
  onlyJiraConfluenceProducts,
} from '../AppsRoute.utils';
import HeaderInjection from '../HeaderInjection';
import SiteSelectorDialogWrapper from '../HeaderInjection/SiteSelectorDialogWrapper';
import { SITE_SELECTOR_VISIBLE_QUERY_PARAM, useCTAClickHandler } from '../hooks/useCTAClickHandler';
import {
  getMigrationAssistanceStatus,
  getMigrationInformationAvailable,
  hasNewerPrivateVersion,
  toAnalyticsHosting,
} from '../utils';

import type {
  AppListingTabsType,
  AppVersion,
  HostingOptionKindType,
  MarketplaceAppByKeyQuery,
} from '@atlassian/mpac-app-listing';
import type { AppListingCloudMigration, BootstrapLinks } from '@atlassian/mpac-types';
import type { AtlassianProductHostingType } from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';

type BeforeHeaderContentProps = {
  appId: string,
  partnerId: string,
  links: BootstrapLinks,
  mpacBaseUrl: string,
  appDetails: $NonMaybeType<$PropertyType<MarketplaceAppByKeyQuery, 'marketplaceAppByKey'>>,
  showOnlyPublic: boolean,
};

const BeforeHeaderContent = ({
  links,
  partnerId,
  appId,
  mpacBaseUrl,
  appDetails,
  showOnlyPublic,
}: BeforeHeaderContentProps) => {
  const hasNewerPvtVersionForAnyHosting =
    hasNewerPrivateVersion(appDetails.privateCloudVersion, appDetails.publicCloudVersion) ||
    hasNewerPrivateVersion(appDetails.privateServerVersion, appDetails.publicServerVersion) ||
    hasNewerPrivateVersion(appDetails.privateDataCenterVersion, appDetails.publicDataCenterVersion);

  return (
    <>
      <AdminHeaderBanners
        appManageLink={links.appManageLink.expand({ appId })}
        hasNewerPrivateVersion={hasNewerPvtVersionForAnyHosting}
        listingStatus={appDetails.listingStatus}
        mpacBaseUrl={mpacBaseUrl}
        partnerId={partnerId}
        showOnlyPublic={showOnlyPublic}
      />
      <AdminHeaderLinks
        appManageLink={links.appManageLink.expand({ appId })}
        appShortLink={links.appShortLink.expand({ appId })}
        mpacBaseUrl={mpacBaseUrl}
        partnerId={partnerId}
      />
    </>
  );
};

type AppListingPageContentProps = {|
  appDetails: $NonMaybeType<$PropertyType<MarketplaceAppByKeyQuery, 'marketplaceAppByKey'>>,
  appId: string,
  setNewTab: (tab: AppListingTabsType) => void,
  setNewHostingType: (hostingType: AtlassianProductHostingType) => void,
  selectedHosting: AtlassianProductHostingType,
  selectedAppVersion: AppVersion,
  tab: AppListingTabsType,
  links: BootstrapLinks,
  showOnlyPublic: boolean,
|};

// eslint-disable-next-line max-lines-per-function
const AppListingPageContent = (props: {
  ...AppListingPageContentProps,
  cloudMigrationInfo: ?AppListingCloudMigration,
}) => {
  const { isLoggedIn } = useLoggedInUser();
  const {
    appDetails,
    appId,
    selectedHosting,
    selectedAppVersion,
    tab,
    showOnlyPublic,
    links,
    cloudMigrationInfo,
  } = props;
  const {
    mpacBaseUrl,
    jiraServiceDeskEmbedBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
  } = BootstrapConfig.get();
  const fireAnalyticsEvent = useAnalyticsEvents();
  const { enableWebpImage } = useFeatureFlags();

  const serverAppVersion = appDetails.serverAppVersions.edges?.[0]?.node;
  const dataCenterAppVersion = appDetails.dataCenterAppVersions.edges?.[0]?.node;
  const cloudAppVersion = appDetails.cloudAppVersions.edges?.[0]?.node;
  const versionInfo = {
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  };

  useAppListingPageAnalytics({
    appName: appDetails.name,
    appId,
  });

  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: appDetails.googleAnalyticsId,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  useGoogleAnalyticsExtraContext({
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  });

  useLayoutEffect(() => {
    Telemetry.timeToFirstMeaningfulPaint(performance.now());
  }, []);

  const instrumentHostingClick = (
    hosting: AtlassianProductHostingType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    fireAnalyticsEvent({
      eventType: MenuItemClickedEvent,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        label: toAnalyticsHosting(hosting),
        hosting: toAnalyticsHosting(hosting),
        selectionType: hostingOptionKind,
      },
    });
  };

  const handleTabChange = (newTab: AppListingTabsType) => {
    props.setNewTab(newTab);
    fireAnalyticsEvent({
      eventType: TabClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        label: capitalize(newTab),
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        appListingTabKey: capitalize(newTab),
      },
    });
  };

  const handleHostingChange = hosting => {
    props.setNewHostingType(hosting);
  };

  const handleVendorContactClick = (hostingType: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        source: hostingType,
      },
    });
  };

  const handleCloudMigrationResourcesLinksClick = (label: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
      source: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
        label,
      },
    });
  };

  const handleSupportLinkClick = supportLink => {
    safeToForwardToPartner(appId, () => {
      trackVendorSupportLinkClick(supportLink);
    });
  };

  const handleMediaViewerOpen = (resourceType, imageUrl) => {
    safeToForwardToPartner(appId, () => {
      trackLightboxView(imageUrl, resourceType === MediaViewerResource.Highlight, mpacCdnBaseUrl);
    });
  };

  const handleWatchAppToggle = toggleOn => {
    safeToForwardToPartner(appId, () => {
      trackWatchAction(toggleOn ? 'Watch' : 'Unwatch');
    });
  };

  const handleSeeMoreReviewsClick = () => {
    safeToForwardToPartner(appId, () => {
      trackSeeMoreReviews();
    });
  };

  const handleArtifactDownloadClick = url => {
    const { pathname } = new URL(url);
    safeToForwardToPartner(appId, () => {
      trackDownloadLink(pathname);
    });
  };

  const handleHostingSelectOpen = () => {
    fireAnalyticsEvent({
      eventType: MenuExpandedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        hostingOptions: appDetails.productHostingOptions.map(toAnalyticsHosting),
      },
    });
  };

  const handleHostingOptionClick = (hosting, hostingOptionKind) => {
    instrumentHostingClick(hosting, hostingOptionKind);

    if (hosting === AtlassianProductHostingTypeValues.DataCenter) {
      safeToForwardToPartner(appId, () => {
        trackLookingForDataCenter();
      });
    }
  };

  const handleMigrationLozengeClick = () => {
    handleTabChange(AppListingTab.Overview);
    setTimeout(() => {
      DOMUtils.scrollToAndFocusElement(`#${ELEMENT_ID.CLOUD_MIGRATION_SECTION_LINKS}`);
    }, 0);
    fireAnalyticsEvent({
      eventType: LozengeClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      },
    });
  };

  const {
    siteSelectorOpen,
    onSiteSelectorClose,
    hostingSelectorOpen,
    onHostingSelectorClose,
    activeCTAKind,
    hostingSelectorPurchaseIntent,
    onRequestAppClick,
    onAppListingHeaderCTAClick,
    onHostingSelectorCTAClick,
    onPricingCalculatorCTAClick,
  } = useCTAClickHandler({
    appKey: appDetails.appKey,
    appId,
    userLoggedIn: isLoggedIn,
    selectedHosting,
    paymentModel: selectedAppVersion.paymentModel,
    compatibleProducts: selectedAppVersion.deployment.compatibleProducts,
    cloudAppVersion,
    artifactDownloadLink: links.artifactDownload,
  });

  const getHeaderInjection = () => {
    const hasPublicCloudVersion = !!appDetails.publicCloudVersion.edges?.length;
    const hasPrivateCloudVersion = !!appDetails.privateCloudVersion.edges?.length;

    // An app is private remoteable only, if it has no public version
    // and at-least one private version
    const isPrivateRemoteableOnly = !hasPublicCloudVersion && hasPrivateCloudVersion;
    return (
      <HeaderInjection
        appKey={appDetails.appKey}
        appId={appId}
        appName={appDetails.name}
        selectedHosting={selectedHosting}
        privateListingsLink={links.managePrivateListing}
        privateRemotableOnly={isPrivateRemoteableOnly}
        appDetailsLink={links.appDetails}
        artifactDownloadLink={links.artifactDownload}
        trialLicenseActivationLink={links.trialLicenseActivation}
        licensePurchaseLink={links.licensePurchase}
        versionInfo={versionInfo}
        productHostingOptions={appDetails.productHostingOptions}
        hostingSelectorOpen={hostingSelectorOpen}
        onHostingSelectorClose={onHostingSelectorClose}
        activeCTAKind={activeCTAKind}
        hostingSelectorPurchaseIntent={hostingSelectorPurchaseIntent}
        onRequestAppClick={onRequestAppClick}
        onAppListingHeaderCTAClick={onAppListingHeaderCTAClick}
        onHostingSelectorCTAClick={onHostingSelectorCTAClick}
      />
    );
  };

  const renderBeforeHeader = (partnerId: ?string) => {
    if (!isLoggedIn || !partnerId) {
      return null;
    }
    return (
      <BeforeHeaderContent
        appId={appId}
        partnerId={partnerId}
        appDetails={appDetails}
        mpacBaseUrl={mpacBaseUrl}
        links={links}
        showOnlyPublic={showOnlyPublic}
      />
    );
  };

  const metaTagImage =
    selectedAppVersion.heroImage ||
    (selectedAppVersion.highlights?.length && selectedAppVersion.highlights[0].croppedScreenshot) ||
    appDetails.logo;

  const jiraConfluenceCloudAppCompatibilities = cloudAppVersion
    ? onlyJiraConfluenceProducts(selectedAppVersion.deployment.compatibleProducts).map(
        product => product.id
      )
    : [];

  const installAppHrefResolver = selectedSiteProduct =>
    getEmceeAppListingInstallLink(appDetails.appKey, selectedSiteProduct);

  const requestAppHrefResolver = selectedSiteProduct =>
    getEmceeAppListingRequestAppLink(appDetails.appKey, selectedSiteProduct);

  const loginRedirectPath = UrlUtils.updateQueryParam(
    window.location.href,
    SITE_SELECTOR_VISIBLE_QUERY_PARAM,
    'true'
  );

  return (
    <>
      <AppListingMeta
        appId={appDetails.appId}
        appName={appDetails.name}
        appSlug={appDetails.slug}
        appDescription={appDetails.tagline}
        canonicalLink={links.appListingCanonical}
        mediaImageLink={enableWebpImage ? links.mediaImage : links.mediaFile}
        image={metaTagImage}
        logo={appDetails.logo}
      />

      <SiteSelectorDialogWrapper
        installAppHrefResolver={installAppHrefResolver}
        requestAppHrefResolver={requestAppHrefResolver}
        appCompatibilities={jiraConfluenceCloudAppCompatibilities}
        appKey={appDetails.appKey}
        appName={appDetails.name}
        paymentModel={selectedAppVersion.paymentModel}
        appDetailsLink={links.appDetails}
        onClose={onSiteSelectorClose}
        isOpen={siteSelectorOpen}
        loginRedirectPath={loginRedirectPath}
        mpacBaseUrl={mpacBaseUrl}
      />

      <AppListing
        appDetails={appDetails}
        components={{ beforeHeader: renderBeforeHeader(appDetails.partner?.id) }}
        userLoggedIn={isLoggedIn}
        loginHref={`/login?redirect=${encodeURIComponent(window.location.href)}`}
        mpacBaseUrl={mpacBaseUrl}
        atlassianCommunityBaseUrl={atlassianCommunityBaseUrl}
        headerInjection={getHeaderInjection()}
        links={links}
        selectedHosting={selectedHosting}
        selectedTab={tab}
        cloudMigration={cloudMigrationInfo}
        onHostingChange={handleHostingChange}
        onTabSelect={handleTabChange}
        onSupportLinkClick={handleSupportLinkClick}
        onMediaViewerOpen={handleMediaViewerOpen}
        onWatchAppToggle={handleWatchAppToggle}
        onSeeMoreReviewsClick={handleSeeMoreReviewsClick}
        onArtifactDownloadClick={handleArtifactDownloadClick}
        onHostingSelectOpen={handleHostingSelectOpen}
        onHostingOptionClick={handleHostingOptionClick}
        onContactPartnerClick={handleVendorContactClick}
        onCloudMigrationResourceLinksClick={handleCloudMigrationResourcesLinksClick}
        onMigrationLozengeClick={handleMigrationLozengeClick}
        openMarketplaceLinksInNewTab={false}
        showHostingSelect
        showResourcesSection
        showInstallationTab
        showVersionsTab
        showReviewsTab
        showInstallationCount
        showIntegrationsDetailsSection
        showPricingTab
        showHeaderProductCompatibilities
        showHeaderLozenges
        showCloudMigrationLozenge={true}
        onPricingCalculatorInstallCTAClick={onPricingCalculatorCTAClick}
      />

      {props.appDetails.jsdWidgetKey && (
        <AppListingJSDWidget
          jiraServiceDeskEmbedBaseUrl={jiraServiceDeskEmbedBaseUrl}
          jsdWidgetKey={props.appDetails.jsdWidgetKey}
        />
      )}
    </>
  );
};

const AppListingWithAnalyticsContext = (props: AppListingPageContentProps) => {
  const { appDetails, appId, selectedHosting, tab, selectedAppVersion } = props;
  const hosting = HostingTypeToQueryParam[selectedHosting];
  const { appKey } = appDetails || {};
  const paymentModel =
    selectedAppVersion.paymentModel === MarketplaceAppPaymentModelValues.Free ? 'free' : 'paid';
  const isEcosystemCloudApp =
    selectedAppVersion.deployment.__typename === AppDeployment.ECOSYSTEM_CLOUD;
  const { mpacBaseUrl } = BootstrapConfig.get();
  const cloudMigrationInfo = useCloudMigrationInfo(mpacBaseUrl, appKey);

  const analyticsContext = {
    appId,
    appKey,
    appListingTabKey: tab,
    category: ANALYTICS_CATEGORY_APP_LISTING,
    hosting,
    isEcosystemCloudApp,
    paymentModel,
    alternateCloudAppKey: cloudMigrationInfo.alternateCloudAppKey,
    migrationAssistanceStatus: getMigrationAssistanceStatus(cloudMigrationInfo),
    migrationInformationAvailable: getMigrationInformationAvailable(cloudMigrationInfo),
  };

  return (
    <MarketplaceAnalyticsContext attributes={analyticsContext}>
      <AppListingPageContent {...props} cloudMigrationInfo={cloudMigrationInfo} />
    </MarketplaceAnalyticsContext>
  );
};

export default AppListingWithAnalyticsContext;
